import type { DropDownNoSelection, DropDownResult, JsonData } from '@myosh/odin-components';
import {
  Button,
  Checkbox,
  DropDown,
  DropDownMultiSelect,
  isDropDownResult,
  Loader,
  OdinCalendar,
  OdinController,
  OdinIcon,
  OdinIconSize,
  Popover,
  PopoverContent,
  PopoverTrigger,
  TabViewPanel,
} from '@myosh/odin-components';
import { t } from 'i18next';
import { orderBy } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { odinIconStyle } from '../../constants/common-style-constants';
import { useDateRangeContext } from '../../context/globalDateRange.context';
import { getDashboardFiltersCombination } from '../../helpers/dashboard-filter-formatter';
import { appendTimeUnit, formatDate } from '../../helpers/dateFormater';
import { getDropDownFilterValue } from '../../helpers/getDropDownFilterValue';
import { getValueOrDefault } from '../../helpers/getvalueOrDefault';
import { useConfiguredDateRanges } from '../../hooks/useConfiguredDateRanges';
import { useDashboardUser } from '../../hooks/useDashboardUser';
import type { DateFilterRangeType } from '../../redux/config/filters-api';
import { useCreateUserAppliedFiltersMutation } from '../../redux/config/filters-api';
import { useGetHierarchyWithValueQuery } from '../../redux/config/hierarchy';
import { addHierarchyFilter } from '../../redux/slices/globalFilter/GlobalFilterHierarchySlice';
import type {
  GlobalFilterDateDetails,
  GlobalFilterDetails,
  GlobalFilterState,
} from '../../redux/slices/globalFilter/global-dates-filter-slice';
import { selectSearchQuery, setGlobalDateFilters } from '../../redux/slices/globalFilter/global-dates-filter-slice';
import type { Hierarchy, HierarchyFilters, HierarchyFilterValues, HierarchyValue } from '../../shared/Hierarchy';
import { useSelectedView } from '../Charts/hooks/use-selected-view';
import FilterChips, { FilterChip } from '../common/filter-chips.component';
import DashboardTabView from '../DashboardTabView/dashboard-tab-view';
import { TopMenuComponent } from '../TopMenu/top-menu-component';
import type { DateRange, MainLayoutProps } from './Header.interface';
import RunningJobsPanel from './running-jobs-panel.component';
import { ToggleEditComponent } from './toggle-edit.component';
import { quickDateRange } from './utilities';
import { ViewHandlerComponent } from './view-components/view-handler.component';
import { ViewMenu } from './view-components/view-menu';

const DateDetailsGroupName = 'DateDetails';
const DateDetailsFieldNames = {
  dateStart: 'DateDetails.dateStart',
  dateEnd: 'DateDetails.dateEnd',
  quickDateRange: 'DateDetails.quickDateRange',
};

export const Header = ({ classNames }: MainLayoutProps) => {
  const { data: allExistingHierarchies, isLoading } = useGetHierarchyWithValueQuery();
  const [createUserAppliedFilters] = useCreateUserAppliedFiltersMutation();
  const { control, setValue, resetField } = useForm();
  const dispatch = useDispatch();

  //pre-defined ranges
  const dateRanges = useConfiguredDateRanges();
  const currentUser = useDashboardUser();
  const currentView = useSelectedView();
  const dateRangeValue = useDateRangeContext();
  const globalFilterData = useSelector(selectSearchQuery);

  const hierarchyFilterDefaultValue: HierarchyFilters = {
    id: currentUser?.id ?? '',
    values: {},
    showArchived: false,
  };

  const [isHierarchyOpen, setIsHierarchyOpen] = useState<boolean>(false);
  const [dateRangeText, setDateRangeText] = useState<string>('');
  const [filterHasChanged, setFilterHasChanged] = useState(false);
  const [selectedHierarchies, setSelectedHierarchies] = useState<HierarchyFilters>(hierarchyFilterDefaultValue);
  const [localDateStart, setLocalDateStart] = useState<string | undefined>(undefined);
  const [localDateEnd, setLocalDateEnd] = useState<string | undefined>(undefined);

  const activeTabRef = useRef<number>(0);
  const globalDateLocalFilters = useRef<GlobalFilterDetails>(undefined);
  const globalFiltersAssigned = useRef<GlobalFilterState>(undefined);
  const latestFiltersApplied = useRef<HierarchyFilters>(hierarchyFilterDefaultValue);

  const archivedHierarchyNames = useMemo(() => {
    return (
      allExistingHierarchies?.flatMap((hierarchies) =>
        hierarchies.values.filter((value) => value.archived).map((item) => item.name)
      ) ?? []
    );
  }, [allExistingHierarchies]);

  const toggleHierarchyDropdown = useCallback(() => {
    setIsHierarchyOpen((prev) => !prev);
  }, []);

  const closeHierarchyDropdown = () => {
    //Reset to the latest valid values
    setValue(DateDetailsGroupName, globalFiltersAssigned.current?.value?.DateDetails);
    setValue(DateDetailsFieldNames.quickDateRange, globalFiltersAssigned.current?.value?.DateDetails.quickDateRange);
    setSelectedHierarchies(latestFiltersApplied.current);
    globalDateLocalFilters.current = globalFiltersAssigned.current?.value;
    setLocalDateStart(globalFiltersAssigned.current?.value?.DateDetails?.dateStart);
    setLocalDateEnd(globalFiltersAssigned.current?.value?.DateDetails?.dateEnd);
    setIsHierarchyOpen(false);
    activeTabRef.current = 0;
  };

  const hierarchyFilterKeys = useMemo(() => {
    if (selectedHierarchies?.values) {
      const appliedFilterNames = Object.keys(selectedHierarchies.values);
      if (allExistingHierarchies) {
        return orderBy(
          appliedFilterNames.map((filterName) => {
            const weight = allExistingHierarchies.find((hierarchy) => hierarchy.name === filterName)?.weight ?? 0;
            return { hierarchy: filterName, weight };
          }),
          ['weight', 'hierarchy']
        ).map((orderedFilter) => orderedFilter.hierarchy);
      }
      return appliedFilterNames;
    }
    return [];
  }, [selectedHierarchies, allExistingHierarchies]);

  useEffect(() => {
    if (dateRangeValue?.DateDetails) {
      setValue(DateDetailsGroupName, dateRangeValue.DateDetails);
    }
  }, [dateRangeValue, setValue]);

  useEffect(() => {
    if (currentView) {
      if (!currentView.name) return;

      const findDateRangeById = (id: string | undefined) =>
        id ? dateRanges?.find((range) => range.id === id) : undefined;

      const dateRange =
        findDateRangeById(currentView.dashboardFiltersCombination?.dateFiltersForView?.dateFilterRangeType) ??
        findDateRangeById(currentView.dateFiltersForView?.dateFilterRangeType) ??
        findDateRangeById('LAST_12_MONTHS');

      // Normalize id ensuring it is strictly string | number (avoid bigint or other Key variants)
      const normalizedId =
        dateRange?.id !== undefined
          ? typeof dateRange.id === 'number'
            ? dateRange.id
            : String(dateRange.id)
          : undefined;
      const updateDateFilter: GlobalFilterDateDetails = {
        dateStart: configureWithTime(
          currentView.dashboardFiltersCombination?.dateFiltersForView?.fromDate ?? dateRange?.from ?? ''
        ),
        dateEnd: configureWithTime(
          currentView.dashboardFiltersCombination?.dateFiltersForView?.toDate ?? dateRange?.to ?? ''
        ),
        quickDateRange: {
          value: (normalizedId as string | number | undefined) ?? quickDateRange.value,
          text: dateRange?.caption ?? quickDateRange.text,
        },
      };

      createUserAppliedFilters({
        userAppliedFiltersDto: {
          userId: currentUser?.id,
          dashboardFiltersCombination: getDashboardFiltersCombination({
            dateFilterRangeType:
              (updateDateFilter.quickDateRange.value as DateFilterRangeType) ??
              currentView.dateFiltersForView?.dateFilterRangeType,
            fromDate: updateDateFilter.dateStart,
            toDate: updateDateFilter.dateEnd,
            hierarchyValues: currentView.dashboardFiltersCombination?.hierarchyFilters?.hierarchyValues,
            showArchived: currentView.dashboardFiltersCombination?.hierarchyFilters?.showArchived,
          }),
        },
      })
        .then(() => {
          globalFiltersAssigned.current = { value: { DateDetails: updateDateFilter } };
          globalDateLocalFilters.current = { DateDetails: updateDateFilter };

          latestFiltersApplied.current = {
            id: currentUser?.id ?? '',
            values: currentView.dashboardFiltersCombination?.hierarchyFilters?.hierarchyValues ?? {},
            showArchived: currentView.dashboardFiltersCombination?.hierarchyFilters?.showArchived,
          };

          dispatch(setGlobalDateFilters({ DateDetails: updateDateFilter }));

          dispatch(
            addHierarchyFilter(currentView.dashboardFiltersCombination?.hierarchyFilters?.hierarchyValues ?? {})
          );

          setDateRangeText(updateDateFilter.quickDateRange?.text ?? '');
          setValue(DateDetailsGroupName, updateDateFilter);
          setValue(DateDetailsFieldNames.quickDateRange, updateDateFilter.quickDateRange);

          if (currentView?.dashboardFiltersCombination?.hierarchyFilters) {
            setSelectedHierarchies({
              id: currentUser?.id ?? '',
              showArchived: currentView.dashboardFiltersCombination?.hierarchyFilters?.showArchived,
              values: currentView?.dashboardFiltersCombination?.hierarchyFilters?.hierarchyValues ?? {},
            });
          } else {
            setSelectedHierarchies(hierarchyFilterDefaultValue);
          }
        })
        .catch(() => {
          toast.error(t('userAppliedFilterErrorMsg'));
        });
    }
  }, [currentView, createUserAppliedFilters, currentUser?.id, dateRanges, dispatch]);

  const handleFieldChange = (name: string, value: string | DropDownResult) => {
    //make sure the latest values are read
    let formDateDetails = globalDateLocalFilters.current;
    const [groupName, fieldName] = name.split('.');
    const currentDataTab = isDropDownResult(value)
      ? dateRanges?.find((dataTab) => dataTab.id === value.value)
      : undefined;
    //Predefined selection is chosen
    if (fieldName === 'quickDateRange' && currentDataTab) {
      const dateDetails = {
        DateDetails: {
          dateStart: configureWithTime(currentDataTab.from),
          dateEnd: configureWithTime(currentDataTab.to),
          quickDateRange: {
            value: String(currentDataTab.id ?? ''),
            text: currentDataTab.caption || '',
          },
        },
      };
      setValue(DateDetailsGroupName, dateDetails.DateDetails);
      setLocalDateStart(dateDetails.DateDetails.dateStart);
      setLocalDateEnd(dateDetails.DateDetails.dateEnd);

      formDateDetails = dateDetails;
    } else if (
      groupName === DateDetailsGroupName &&
      (fieldName === 'dateStart' || fieldName === 'dateEnd') &&
      formDateDetails
    ) {
      //quick range must be changed to custom if a user selects own range
      setValue(DateDetailsFieldNames.quickDateRange, quickDateRange);
      const newDateStart = fieldName === 'dateStart' ? (value as string) : formDateDetails?.DateDetails?.dateStart;
      const newDateEnd = fieldName === 'dateEnd' ? (value as string) : formDateDetails?.DateDetails?.dateEnd;
      setLocalDateStart(newDateStart);
      setLocalDateEnd(newDateEnd);
      formDateDetails = {
        DateDetails: {
          ...(formDateDetails?.DateDetails ?? {}),
          [fieldName]: value || '',
          quickDateRange,
        },
      };
    } else if (groupName === DateDetailsGroupName && formDateDetails) {
      formDateDetails = {
        DateDetails: {
          ...formDateDetails?.DateDetails,
          [fieldName]: value || '',
        },
      };
    }
    globalDateLocalFilters.current = formDateDetails;
  };

  const convertForDropDownHierarchies = useCallback(
    (values?: HierarchyFilterValues) => {
      const hierarchiesValuesMapLocal: Record<string, DropDownResult[]> = {};
      if (values && allExistingHierarchies) {
        for (let j = 0; j < hierarchyFilterKeys.length; j++) {
          const currentFilterKey = hierarchyFilterKeys[j];
          for (let i = 0; i < allExistingHierarchies.length; i++) {
            if (currentFilterKey === allExistingHierarchies[i].name) {
              hierarchiesValuesMapLocal[currentFilterKey] = allExistingHierarchies[i].values
                .filter((data: HierarchyValue) => values[currentFilterKey].includes(data.name))
                .map((data: HierarchyValue) => {
                  return {
                    value: data.id,
                    text: data.name,
                  } as DropDownResult;
                });
            }
          }
        }
      }
      return hierarchiesValuesMapLocal;
    },
    [allExistingHierarchies, hierarchyFilterKeys]
  );

  const hierarchiesValuesMap: Record<string, DropDownResult[]> = useMemo(() => {
    return convertForDropDownHierarchies(selectedHierarchies?.values);
  }, [selectedHierarchies, convertForDropDownHierarchies]);

  const handleHierarchyFiltersChange = useCallback(
    (name: string, value: DropDownResult[] | DropDownNoSelection | DropDownResult | undefined) => {
      const userId = currentUser?.id;
      if (userId) {
        const currentFilters: HierarchyFilters = {
          id: userId,
          values: {
            ...selectedHierarchies?.values,
            [name]: Array.isArray(value) ? value.map((data: DropDownResult) => data.text) : [],
          },
          showArchived: selectedHierarchies?.showArchived || false,
        };

        Object.keys(currentFilters.values).forEach((key: string) => {
          if (currentFilters.values[key].length === 0) {
            delete currentFilters.values[key];
          }
        });
        setSelectedHierarchies(currentFilters);
      }
    },
    [currentUser?.id, selectedHierarchies]
  );

  useEffect(() => {
    globalFiltersAssigned.current = globalFilterData;
    globalDateLocalFilters.current = globalFilterData.value;
    setDateRangeText(getValueOrDefault(globalFilterData.value?.DateDetails?.quickDateRange?.text, ''));
    setLocalDateStart(globalFilterData.value?.DateDetails?.dateStart);
    setLocalDateEnd(globalFilterData.value?.DateDetails?.dateEnd);
  }, [globalFilterData]);

  const quickDateRangleHandler = (value: DropDownResult) => {
    handleFieldChange(DateDetailsFieldNames.quickDateRange, value as DropDownResult);
  };

  /**
   * Unmount hierarchies when dialog is closed. Makes sure the valid values are shown as selected
   */
  const hierarchiesComponents = useMemo(() => {
    return isHierarchyOpen
      ? allExistingHierarchies?.map((hierarchy: Hierarchy) => (
          <DropDownMultiSelect
            key={hierarchy.name}
            name={hierarchy.name}
            label={hierarchy.name}
            data={hierarchy.values.filter((el) => (selectedHierarchies?.showArchived ? { ...el } : !el.archived))}
            value={hierarchiesValuesMap[hierarchy.name] ?? []}
            allowClear={true}
            placeholder={`Select ${hierarchy.name}`}
            textField="name"
            valueField="id"
            onChange={(value: DropDownResult | DropDownResult[] | DropDownNoSelection | undefined) =>
              handleHierarchyFiltersChange(hierarchy.name, getDropDownFilterValue(value))
            }
            labelStyles="font-bold text-xs text-gray-1"
            className="py-0.5 pl-1.5 text-gray-2"
          />
        ))
      : [];
  }, [
    allExistingHierarchies,
    hierarchiesValuesMap,
    isHierarchyOpen,
    handleHierarchyFiltersChange,
    selectedHierarchies,
  ]);

  const applyClickHandler = useCallback(() => {
    const dateFilter = globalDateLocalFilters.current;
    const hierarchyFilter = selectedHierarchies;

    createUserAppliedFilters({
      userAppliedFiltersDto: {
        userId: currentUser?.id,
        dashboardFiltersCombination: getDashboardFiltersCombination({
          dateFilterRangeType: dateFilter?.DateDetails.quickDateRange.value as DateFilterRangeType,
          fromDate: dateFilter?.DateDetails.dateStart,
          toDate: dateFilter?.DateDetails.dateEnd,
          hierarchyValues: hierarchyFilter?.values,
          showArchived: hierarchyFilter?.showArchived,
        }),
      },
    })
      .then(() => {
        dispatch(setGlobalDateFilters(dateFilter));
        setFilterHasChanged(true);

        latestFiltersApplied.current = hierarchyFilter;
        dispatch(addHierarchyFilter(hierarchyFilter?.values ?? {}));
      })
      .catch(() => {
        toast.error(t('userAppliedFilterErrorMsg'));
      })
      .finally(() => {
        closeHierarchyDropdown();
      });
  }, [createUserAppliedFilters, currentUser?.id, dispatch, selectedHierarchies]);

  const handleChipsClick = useCallback(
    (tabIndex: number, isOpened: boolean) => {
      if (!isOpened) {
        activeTabRef.current = tabIndex;
        toggleHierarchyDropdown();
      }
    },
    [toggleHierarchyDropdown]
  );
  //called from view-save-modal component to disable the restore view menu option
  const viewSaved = () => {
    setFilterHasChanged(false);
  };

  const restoreCurrentView = () => {
    const origHierarchyFilters: HierarchyFilters = {
      id: currentView?.userId ?? '',
      values: currentView?.dashboardFiltersCombination?.hierarchyFilters?.hierarchyValues ?? {},
      showArchived: currentView?.dashboardFiltersCombination?.hierarchyFilters?.showArchived,
    };

    let dateRange: DateRange | undefined;
    if (currentView?.dashboardFiltersCombination?.dateFiltersForView) {
      dateRange = dateRanges?.find(
        (range) => range.id === currentView?.dashboardFiltersCombination?.dateFiltersForView?.dateFilterRangeType
      );
    } else {
      dateRange = dateRanges?.find((range) => range.id === currentView?.dateFiltersForView?.dateFilterRangeType);
    }
    const normalizedRestoreId =
      dateRange?.id !== undefined
        ? typeof dateRange.id === 'number'
          ? dateRange.id
          : String(dateRange.id)
        : undefined;
    const updateDateFilter: GlobalFilterDateDetails = {
      dateStart: configureWithTime(
        currentView?.dashboardFiltersCombination?.dateFiltersForView?.fromDate ?? dateRange?.from ?? ''
      ),
      dateEnd: configureWithTime(
        currentView?.dashboardFiltersCombination?.dateFiltersForView?.toDate ?? dateRange?.to ?? ''
      ),
      quickDateRange: {
        value: (normalizedRestoreId as string | number | undefined) ?? quickDateRange.value,
        text: dateRange?.caption ?? quickDateRange.text,
      },
    };

    createUserAppliedFilters({
      userAppliedFiltersDto: {
        userId: currentUser?.id,
        dashboardFiltersCombination: getDashboardFiltersCombination({
          dateFilterRangeType:
            (currentView?.dashboardFiltersCombination?.dateFiltersForView
              ?.dateFilterRangeType as DateFilterRangeType) ?? currentView?.dateFiltersForView?.dateFilterRangeType,
          fromDate: updateDateFilter.dateStart,
          toDate: updateDateFilter.dateEnd,
          hierarchyValues: currentView?.dashboardFiltersCombination?.hierarchyFilters?.hierarchyValues,
          showArchived: currentView?.dashboardFiltersCombination?.hierarchyFilters?.showArchived,
        }),
      },
    })
      .then(() => {
        latestFiltersApplied.current = origHierarchyFilters;
        setSelectedHierarchies(origHierarchyFilters);

        resetField(DateDetailsFieldNames.quickDateRange, {
          defaultValue: updateDateFilter.quickDateRange,
        });
        resetField(DateDetailsFieldNames.dateStart, { defaultValue: updateDateFilter.dateStart });
        resetField(DateDetailsFieldNames.dateEnd, { defaultValue: updateDateFilter.dateEnd });

        dispatch(setGlobalDateFilters({ DateDetails: updateDateFilter }));
        dispatch(addHierarchyFilter(origHierarchyFilters?.values ?? {}));

        setFilterHasChanged(false);
      })
      .catch(() => {
        toast.error(t('userAppliedFilterErrorMsg'));
      });
  };

  const handleShowArchivedHierarchyChange = (checked: boolean) => {
    const updatedSelectedHierarchies: HierarchyFilters = {
      id: selectedHierarchies?.id ?? '',
      showArchived: checked,
      values: { ...selectedHierarchies?.values },
    };

    //remove archived items from selection
    if (!checked && archivedHierarchyNames && archivedHierarchyNames.length > 0) {
      for (const hierarchyName in updatedSelectedHierarchies.values) {
        const filteredValues = updatedSelectedHierarchies.values[hierarchyName].filter(
          (itemName) => !archivedHierarchyNames.includes(itemName)
        );
        if (filteredValues.length > 0) {
          updatedSelectedHierarchies.values[hierarchyName] = filteredValues;
        } else {
          delete updatedSelectedHierarchies.values[hierarchyName];
        }
      }
    }
    setSelectedHierarchies(updatedSelectedHierarchies);
  };

  return (
    <>
      <div className={classNames}>
        <div className="flex flex-row">
          <ViewHandlerComponent />
          <Popover open={isHierarchyOpen} onOpenChange={setIsHierarchyOpen}>
            <PopoverTrigger>
              <OdinIcon icon="Filter" size={OdinIconSize.Small} className={odinIconStyle} />
            </PopoverTrigger>
            <PopoverContent side="bottom" align="start" className="w-96 min-w-min max-w-lg">
              <div className="max-h-fit">
                {isLoading ? (
                  <div className="flex h-full w-full items-center justify-center">
                    <Loader size="sm" title="Loading" />
                  </div>
                ) : (
                  <>
                    <DashboardTabView activeIndex={activeTabRef.current} hideBorder={true} className="pb-3">
                      <TabViewPanel header="Date Range" panelStyles="overflow-visible">
                        <OdinController
                          name={DateDetailsFieldNames.quickDateRange}
                          control={control}
                          onChange={quickDateRangleHandler}
                          render={({ field: { onChange } }) => {
                            return (
                              <DropDown
                                name={DateDetailsFieldNames.quickDateRange}
                                allowClear={true}
                                allowSearch
                                value={globalDateLocalFilters.current?.DateDetails.quickDateRange}
                                data={dateRanges as JsonData}
                                label="Quick Date Range"
                                placeholder={dateRangeText}
                                setDefaultValue={false}
                                textField="caption"
                                className="mb-4 overflow-hidden py-0.5 pl-1.5 text-sm text-gray-2"
                                valueField="id"
                                onChange={onChange}
                              />
                            );
                          }}
                        />

                        <div className="flex gap-x-2.5 text-sm text-mono-0">
                          <OdinCalendar
                            value={localDateStart ? new Date(localDateStart) : null}
                            label="From"
                            onChange={(value) => {
                              const dateValue = Array.isArray(value) ? value[0] : value;
                              if (dateValue) {
                                handleFieldChange(
                                  DateDetailsFieldNames.dateStart,
                                  configureWithTime(formatDate(dateValue.toISOString()))
                                );
                              }
                            }}
                            showButtonBar
                            appendTo={document.body}
                          />
                          <OdinCalendar
                            value={localDateEnd ? new Date(localDateEnd) : null}
                            label="To"
                            onChange={(value) => {
                              const dateValue = Array.isArray(value) ? value[0] : value;
                              if (dateValue) {
                                handleFieldChange(
                                  DateDetailsFieldNames.dateEnd,
                                  configureWithTime(formatDate(dateValue.toISOString()))
                                );
                              }
                            }}
                            showButtonBar
                            appendTo={document.body}
                          />
                        </div>
                      </TabViewPanel>
                      <TabViewPanel header="Hierarchy">
                        <Checkbox
                          name="showArchivedHierarchies"
                          label={t('showArchivedHierarchies')}
                          onChange={(value) => {
                            if (value) {
                              handleShowArchivedHierarchyChange(value.checked);
                            }
                          }}
                          initialChecked={selectedHierarchies?.showArchived}
                          key={`showArchivedHierarchies-${selectedHierarchies?.showArchived}`}
                        />
                        <div className="flex flex-col mt-2 gap-2">{hierarchiesComponents}</div>
                      </TabViewPanel>
                    </DashboardTabView>
                    <Button onClick={applyClickHandler}>{String(t('applyFilter'))}</Button>
                  </>
                )}
              </div>
            </PopoverContent>
          </Popover>
          <div className="flex flex-1 flex-wrap">
            <div
              className="my-1 flex items-center pl-3"
              onClick={() => {
                handleChipsClick(0, isHierarchyOpen);
              }}
            >
              {dateRangeText && (
                <>
                  <h1 className="text-sm font-normal">Date Range</h1>
                  <FilterChip text={dateRangeText} />
                </>
              )}
            </div>

            <div className="flex flex-1 flex-wrap">
              <div
                className="my-1 flex flex-row items-center pl-3"
                onClick={() => {
                  handleChipsClick(1, isHierarchyOpen);
                }}
              >
                <>
                  <h1 className="text-sm font-normal">Hierarchy</h1>
                  {hierarchyFilterKeys?.length === 0 ? (
                    <FilterChips chips={['All']} />
                  ) : (
                    selectedHierarchies.values &&
                    hierarchyFilterKeys?.map((hierarchy) => (
                      <FilterChip
                        key={hierarchy}
                        text={`${hierarchy} (${selectedHierarchies.values[hierarchy].length ?? 0})`}
                        tooltip={[...(selectedHierarchies.values[hierarchy] ?? [])]
                          .sort((value1, value2) => value1.localeCompare(value2))
                          .join(', ')}
                      />
                    ))
                  )}
                </>
              </div>
              <div className="group relative">
                <div className="mb-0 pl-3.5 pt-2 text-sm">
                  <OdinIcon icon="Ellipsis" size={OdinIconSize.Small} className={odinIconStyle} />
                </div>
                <div className="absolute right-0 z-50 mt-1 hidden rounded-md bg-mono-1 shadow-md group-focus-within:block group-hover:block">
                  <ViewMenu
                    restoreCurrentView={restoreCurrentView}
                    filterHasChanged={filterHasChanged}
                    viewSaved={viewSaved}
                  />
                </div>
              </div>
              <RunningJobsPanel />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ToggleEditComponent />
            <TopMenuComponent />
          </div>
        </div>
      </div>
    </>
  );
};

const configureWithTime = (time?: string | Date): string => {
  return appendTimeUnit(String(time));
};
